import type {
  Currency,
  InvoiceDirection,
  InvoiceDocType,
  InvoiceScanExisting,
  InvoiceScanFile,
  InvoiceScanGroup,
  InvoiceScanReadResult,
  InvoiceScanSave,
  ScanPart,
} from '@worki/shared';

/**
 * Section 3 (Nitzan, 05.10) — the summary screen of the invoices' quick scan, as
 * data: what each row holds, what it still lacks, and what joining, splitting and
 * saving do to the rows. Kept apart from the dialog so every rule here is tested
 * on its own (see `test/invoiceScanDialog.test.tsx`).
 */

/** A file of the scan: picked, or a page off the camera. */
export interface ScanEntry {
  id: string;
  file: File;
  /** An image's object URL, for its thumbnail. Null for a PDF. */
  url: string | null;
  pdf: boolean;
  /** Section 3 — a photograph that measured blurry. Said, never held against it. */
  blurry: boolean;
  /** What the server read off this file on its own, once it has. */
  reading: InvoiceScanFile | null;
  /** Why the server would not take it (`fields['files.<i>']`), or null. */
  refused: string | null;
}

/** The fields a row cannot be saved without, in the order they are named. */
export const REQUIRED = ['amount', 'party', 'issuedAt', 'number'] as const;
export type RequiredField = (typeof REQUIRED)[number];

/** One row of the summary: one invoice, of one file or several. */
export interface ScanRow {
  id: string;
  /** Entry ids, in page order. */
  files: string[];
  /** One-based original pages of a single shared PDF; absent means the complete files. */
  pdfPages?: number[];
  /** Put together from several files, by the server or by "צירוף לחשבונית הקודמת". */
  joined: boolean;
  /** What the server read off the row's files together, which the prefill swaps between. */
  read: InvoiceScanFile;
  existing: InvoiceScanGroup['existing'];
  direction: InvoiceDirection;
  /** The supplier of an expense, the customer of income. */
  party: string;
  /** Typed by the owner, so switching the direction leaves it alone. */
  partyTyped: boolean;
  number: string;
  /** As typed: a decimal number, or blank. */
  amount: string;
  currency: Currency;
  /** `YYYY-MM-DD`, or blank. */
  issuedAt: string;
  docType: InvoiceDocType | null;
  allocationNumber: string | null;
  /** The save came back 409 for this row: the archive already holds it. */
  conflict: boolean;
  /** What the server refused, by field (`invoices.<i>.<field>`), and `files` for a file. */
  errors: Partial<Record<RequiredField | 'files' | 'currency', string>>;
  /** Expanded to edit. A row that lacks something opens that way. */
  open: boolean;
}

let rowSeq = 0;
const nextRowId = () => `row-${(rowSeq += 1)}`;

const BLANK_READ: InvoiceScanFile = {
  supplier: null,
  customer: null,
  number: null,
  amount: null,
  currency: null,
  issuedAt: null,
  docType: null,
  allocationNumber: null,
  pages: 1,
};

const NO_EXISTING: InvoiceScanGroup['existing'] = { expense: null, income: null };

/** The party the reading offers for a direction: the supplier of an expense, the customer of income. */
export const partyFrom = (read: InvoiceScanFile, direction: InvoiceDirection): string =>
  (direction === 'income' ? read.customer : read.supplier) ?? '';

/** A row as the server read it, an expense until the owner says otherwise. */
export function rowFrom(
  read: InvoiceScanFile,
  files: string[],
  joined: boolean,
  existing: InvoiceScanGroup['existing'] = NO_EXISTING,
  pdfPages?: readonly number[],
): ScanRow {
  const row: ScanRow = {
    id: nextRowId(),
    files,
    ...(pdfPages ? { pdfPages: [...pdfPages] } : {}),
    joined,
    read,
    existing,
    direction: 'expense',
    party: partyFrom(read, 'expense'),
    partyTyped: false,
    number: read.number ?? '',
    amount: read.amount !== null && read.amount > 0 ? String(read.amount) : '',
    currency: read.currency ?? 'ILS',
    issuedAt: read.issuedAt ?? '',
    docType: read.docType,
    allocationNumber: read.allocationNumber,
    conflict: false,
    errors: {},
    open: false,
  };
  return { ...row, open: missingOf(row).length > 0 };
}

/** The rows of a read: one per group, its files by the entry ids sent in that order. */
export function rowsFromRead(result: InvoiceScanReadResult, entryIds: readonly string[]): ScanRow[] {
  return result.groups.map((group) =>
    rowFrom(
      group.read,
      group.files.map((index) => entryIds[index]).filter((id): id is string => Boolean(id)),
      group.joined && group.files.length > 1,
      group.existing,
      group.pdfPages,
    ),
  );
}

/** The amount typed, as a number, or null where it is not a positive one. */
export function amountOf(row: Pick<ScanRow, 'amount'>): number | null {
  const value = Number(row.amount.trim().replace(',', '.'));
  return row.amount.trim() !== '' && Number.isFinite(value) && value > 0 ? value : null;
}

/** A `YYYY-MM-DD` that names a real day. */
export function isDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const at = new Date(year, month - 1, day);
  return at.getFullYear() === year && at.getMonth() === month - 1 && at.getDate() === day;
}

/** What the row lacks before it can be saved, in the order they are named. */
export function missingOf(row: ScanRow): RequiredField[] {
  return REQUIRED.filter((field) => {
    if (field === 'amount') return amountOf(row) === null;
    if (field === 'issuedAt') return !isDay(row.issuedAt);
    return row[field].trim() === '';
  });
}

/** A date as `YYYY-MM-DD` in the device's own day. */
export const dayString = (at: Date): string =>
  `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;

/** Section 2 — dated before the archive starts, so the list would hide it once saved. */
export const beforeArchive = (row: Pick<ScanRow, 'issuedAt'>, archiveStart: Date): boolean =>
  isDay(row.issuedAt) && row.issuedAt < dayString(archiveStart);

const normalise = (value: string | null): string => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const israelDay = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' });

/**
 * What the archive already holds that this row is, the way the server matches it:
 * the same party and the same number, the same way round. Null when nothing is —
 * including once the party or the number has been corrected away from it.
 */
export function duplicateOf(row: ScanRow): InvoiceScanExisting | null {
  const existing = row.existing[row.direction];
  if (!existing) return null;
  if (existing.currency !== undefined && existing.currency !== row.currency) return null;
  if (existing.amount !== null && amountOf(row) !== null && existing.amount !== amountOf(row)) return null;
  if (existing.issuedAt && (existing.source === 'manual' ? israelDay.format(new Date(existing.issuedAt)) : existing.issuedAt.slice(0, 10)) !== row.issuedAt) return null;
  return normalise(existing.supplier) === normalise(row.party) && normalise(existing.number) === normalise(row.number)
    ? existing
    : null;
}

/** In the list already, by the reading or by the save's 409. */
export const isDuplicate = (row: ScanRow): boolean => row.conflict || duplicateOf(row) !== null;

/** Why the row cannot be saved as it is: nothing, when it can. */
export function blockedBy(row: ScanRow, archiveStart: Date): Array<'missing' | 'archive' | 'duplicate' | 'server'> {
  const reasons: Array<'missing' | 'archive' | 'duplicate' | 'server'> = [];
  if (missingOf(row).length > 0) reasons.push('missing');
  if (beforeArchive(row, archiveStart)) reasons.push('archive');
  if (isDuplicate(row)) reasons.push('duplicate');
  if (row.errors.files) reasons.push('server');
  return reasons;
}

export type EditableField = 'party' | 'number' | 'amount' | 'currency' | 'issuedAt';

/** A field changed by hand. What the server said about it is no longer about it. */
export function edit(row: ScanRow, field: EditableField, value: string): ScanRow {
  const errors = { ...row.errors };
  delete errors[field];
  const next: ScanRow = { ...row, errors };
  if (field === 'currency') next.currency = value as Currency;
  else next[field] = value;
  if (field === 'party') next.partyTyped = true;
  // A 409 was about the party and the number it was sent with.
  if (field === 'party' || field === 'number') next.conflict = false;
  return next;
}

/**
 * Section 5 — expense or income. The party the reading offers swaps with it
 * (the supplier of an expense is the business itself on income, and the
 * customer is what is after "לכבוד"), unless the owner typed one.
 */
export function setDirection(row: ScanRow, direction: InvoiceDirection): ScanRow {
  if (row.direction === direction) return row;
  const errors = { ...row.errors };
  if (!row.partyTyped) delete errors.party;
  const next: ScanRow = {
    ...row,
    direction,
    party: row.partyTyped ? row.party : partyFrom(row.read, direction),
    errors,
    // Kept the other way round is not kept this way round: it is another row of the archive.
    conflict: false,
  };
  return missingOf(next).length > 0 ? { ...next, open: true } : next;
}

/**
 * "הפרדה" — a joined row as one row per file again, each from what the server
 * read off that file alone. What the archive matched stays on each, and shows
 * only where that file's own party and number still match it.
 */
export function split(row: ScanRow, entries: readonly ScanEntry[]): ScanRow[] {
  if (row.pdfPages || row.files.length < 2) return [row];
  return row.files.map((id) => {
    const entry = entries.find((candidate) => candidate.id === id);
    const own = rowFrom(entry?.reading ?? { ...BLANK_READ }, [id], false, row.existing);
    return row.direction === 'expense' ? own : setDirection(own, row.direction);
  });
}

const filled = (value: string | null | undefined): boolean => value !== null && value !== undefined && value.trim() !== '';

/** A page-selected invoice must stay independent of every neighboring row. */
export function canJoinPrevious(rows: readonly ScanRow[], index: number): boolean {
  const previous = rows[index - 1];
  const current = rows[index];
  return Boolean(previous && current && !previous.pdfPages && !current.pdfPages);
}

/** Drop only entries no remaining sibling still references. The caller releases their URLs. */
export function removeScanRow(rows: readonly ScanRow[], entries: readonly ScanEntry[], id: string): {
  rows: ScanRow[]; entries: ScanEntry[]; removedEntries: ScanEntry[];
} {
  const removed = rows.find((row) => row.id === id);
  const remaining = rows.filter((row) => row.id !== id);
  const retained = new Set(remaining.flatMap((row) => row.files));
  const removedEntries = entries.filter((entry) => removed?.files.includes(entry.id) && !retained.has(entry.id));
  const dropped = new Set(removedEntries.map((entry) => entry.id));
  return { rows: remaining, entries: entries.filter((entry) => !dropped.has(entry.id)), removedEntries };
}

/**
 * "צירוף לחשבונית הקודמת" — the row at `index` becomes more pages of the one
 * before it. The previous row's values win, and what it lacks is filled from
 * this one.
 */
export function joinPrevious(rows: readonly ScanRow[], index: number): ScanRow[] {
  const previous = rows[index - 1];
  const current = rows[index];
  if (!previous || !current || !canJoinPrevious(rows, index)) return [...rows];

  const pick = (mine: string, theirs: string) => (filled(mine) ? mine : theirs);
  const pickNull = (mine: string | null, theirs: string | null) => (filled(mine) ? mine : theirs);
  const read: InvoiceScanFile = {
    supplier: pickNull(previous.read.supplier, current.read.supplier),
    customer: pickNull(previous.read.customer, current.read.customer),
    number: pickNull(previous.read.number, current.read.number),
    amount: previous.read.amount ?? current.read.amount,
    currency: previous.read.currency ?? current.read.currency,
    issuedAt: previous.read.issuedAt ?? current.read.issuedAt,
    docType: previous.read.docType ?? current.read.docType,
    allocationNumber: pickNull(previous.read.allocationNumber, current.read.allocationNumber),
    pages: previous.read.pages + current.read.pages,
  };
  const party = pick(previous.party, current.party);
  const joined: ScanRow = {
    ...previous,
    files: [...previous.files, ...current.files],
    joined: true,
    read,
    existing: {
      expense: previous.existing.expense ?? current.existing.expense,
      income: previous.existing.income ?? current.existing.income,
    },
    party,
    partyTyped: filled(previous.party) ? previous.partyTyped : current.partyTyped,
    number: pick(previous.number, current.number),
    amount: amountOf(previous) !== null ? previous.amount : current.amount,
    currency: amountOf(previous) !== null ? previous.currency : current.currency,
    issuedAt: isDay(previous.issuedAt) ? previous.issuedAt : current.issuedAt,
    docType: previous.docType ?? current.docType,
    allocationNumber: pickNull(previous.allocationNumber, current.allocationNumber),
    conflict: false,
    errors: {},
  };
  const next = { ...joined, open: missingOf(joined).length > 0 || previous.open };
  return [...rows.slice(0, index - 1), next, ...rows.slice(index + 1)];
}

/** Each row as a part of the totals, where it has an amount. */
export const partsOf = (rows: readonly ScanRow[]): ScanPart[] =>
  rows.flatMap((row) => {
    const amount = amountOf(row);
    return amount === null ? [] : [{ supplier: row.party || null, number: row.number || null, currency: row.currency, amount }];
  });

export type FolderChoice = { kind: 'none' } | { kind: 'existing'; id: string } | { kind: 'new'; name: string };

/**
 * What the save is sent: the files the rows hold, in row and page order, and the
 * manifest that names them by their place in that list. A file of a removed row
 * is not sent at all. `rowOfFile` is which row each file sent belongs to, to put
 * a refusal of `files.<i>` back on its row.
 */
export function manifestOf(
  rows: readonly ScanRow[],
  entries: readonly ScanEntry[],
  folder: FolderChoice,
): { files: File[]; manifest: InvoiceScanSave; rowOfFile: number[]; rowsOfFile: number[][] } {
  const files: File[] = [];
  const rowOfFile: number[] = [];
  const rowsOfFile: number[][] = [];
  const indexOfEntry = new Map<string, number>();
  const invoices = rows.map((row, at) => {
    const indexes: number[] = [];
    for (const id of row.files) {
      const entry = entries.find((candidate) => candidate.id === id);
      if (!entry) continue;
      let index = indexOfEntry.get(id);
      if (index === undefined) {
        index = files.length;
        indexOfEntry.set(id, index);
        files.push(entry.file);
        rowOfFile.push(at);
        rowsOfFile.push([]);
      }
      indexes.push(index);
      if (!rowsOfFile[index]!.includes(at)) rowsOfFile[index]!.push(at);
    }
    return {
      files: indexes,
      ...(row.pdfPages ? { pdfPages: [...row.pdfPages] } : {}),
      direction: row.direction,
      party: row.party.trim(),
      number: row.number.trim(),
      amount: amountOf(row) ?? 0,
      currency: row.currency,
      issuedAt: row.issuedAt,
      docType: row.docType,
      allocationNumber: row.allocationNumber,
    };
  });
  return {
    files,
    manifest: {
      invoices,
      folder:
        folder.kind === 'existing' ? { id: folder.id } : folder.kind === 'new' ? { name: folder.name.trim() } : null,
    },
    rowOfFile,
    rowsOfFile,
  };
}

const FIELD_OF: Record<string, RequiredField | 'currency' | 'files'> = {
  party: 'party',
  number: 'number',
  amount: 'amount',
  issuedAt: 'issuedAt',
  currency: 'currency',
  files: 'files',
  pdfPages: 'files',
};

/**
 * The save's refusal put back on the rows it was about: `invoices.<i>.<field>`
 * on that field, `invoices.<i>` (409) as "already in the list", and `files.<n>`
 * on the row that file belongs to. Each row named opens, so what is wrong is in
 * view. What is left over (`folder`, `manifest`, `files`) is returned to be said
 * above the rows.
 */
export function errorsOnto(
  rows: readonly ScanRow[],
  fields: Record<string, string> | undefined,
  rowOfFile: readonly (number | readonly number[])[],
): { rows: ScanRow[]; general: string[] } {
  const next = rows.map((row) => ({ ...row, errors: { ...row.errors } }));
  const general: string[] = [];
  // Put source-file errors on every sibling first; row-specific errors are more precise.
  const entries = Object.entries(fields ?? {});
  for (const [key, code] of [...entries.filter(([key]) => /^files\.\d+$/.test(key)), ...entries.filter(([key]) => !/^files\.\d+$/.test(key))]) {
    const invoice = /^invoices\.(\d+)(?:\.(\w+))?/.exec(key);
    const file = /^files\.(\d+)$/.exec(key);
    if (invoice) {
      const row = next[Number(invoice[1])];
      if (!row) continue;
      row.open = true;
      if (!invoice[2]) row.conflict = true;
      else {
        const field = FIELD_OF[invoice[2]];
        if (field) row.errors[field] = code;
        else general.push(code);
      }
    } else if (file) {
      const affected = rowOfFile[Number(file[1])];
      for (const index of typeof affected === 'number' ? [affected] : affected ?? []) {
        const row = next[index];
        if (!row) continue;
        row.open = true;
        row.errors.files = code;
      }
    } else {
      general.push(code);
    }
  }
  return { rows: next, general };
}
